/**
 * CDN token + stream-proxy helpers for the Live TV player.
 * Ported from kitongatv's lib/api.ts, but backed by the i-net backend
 * (GET /api/token) instead of Firebase.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.i-nettz.site/api';

let cachedToken = null;
let tokenExpiryTime = 0;
let tokenIsExpired = false;
let refreshTimer = null;
let onTokenRefreshed = null;

function toBase64Url(str) {
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4) payload += '=';
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

async function fetchServerToken() {
  try {
    const r = await fetch(`${API_BASE}/token`, { cache: 'no-store' });
    if (!r.ok) return null;
    const data = await r.json();
    if (data && data.token) return { value: data.token, ts: Date.now() };
  } catch {}
  return null;
}

async function refreshToken() {
  const serverResult = await fetchServerToken();
  const value = serverResult && serverResult.value ? serverResult.value : '';
  cachedToken = value;
  const payload = decodeJwtPayload(cachedToken);
  if (payload && payload.exp) {
    const expMs = Number(payload.exp) * 1000;
    tokenIsExpired = Date.now() > expMs;
    tokenExpiryTime = expMs - 60000;
  } else {
    tokenExpiryTime = Date.now() + 300000;
    tokenIsExpired = !value;
  }
  if (onTokenRefreshed) onTokenRefreshed(cachedToken);
  return cachedToken;
}

export async function getCdnToken() {
  if (cachedToken && Date.now() < tokenExpiryTime && !tokenIsExpired) return cachedToken;
  return refreshToken();
}

export function isTokenExpired() {
  return tokenIsExpired;
}

export function onTokenUpdate(cb) {
  onTokenRefreshed = cb;
}

export function startTokenRefresh() {
  refreshToken().catch(() => {});
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = setInterval(() => {
    refreshToken().catch(() => {});
  }, 60000);
  return () => {
    if (refreshTimer) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
  };
}

export function proxyUrl(targetUrl, cdntoken) {
  const b64 = toBase64Url(targetUrl);
  const base = `${API_BASE}/proxy/${b64}`;
  return cdntoken ? `${base}?cdntoken=${encodeURIComponent(cdntoken)}` : base;
}

const CDN_TOKEN_DOMAINS = [
  'azamtvltd.co.tz',
  'zapitv.com',
  'edgenextcdn.net',
];

const CORS_ALLOWED_DOMAINS = [
  'azamtvltd.co.tz',
  'zapitv.com',
];

const IP_RESTRICTED_DOMAINS = [
  'azamtvltd.co.tz',
  'edgenextcdn.net',
];

export function needsCdnToken(url) {
  return CDN_TOKEN_DOMAINS.some((d) => url.includes(d));
}

export function isCorsAllowed(url) {
  return CORS_ALLOWED_DOMAINS.some((d) => url.includes(d));
}

export function isIpRestricted(url) {
  return IP_RESTRICTED_DOMAINS.some((d) => url.includes(d));
}
